import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { getDocuments, getFields, submitFilesOnly, validateFreeService } from '../../services/api.service';
import DocumentUploadSection from '../../components/file-upload-section';
import TitleQuotation from '../../components/title-quotation';
import Hr from '../../components/Style/hr';
import Helper from '../../services/helper.service';
import Loading from '../../components/Loading/loading';
import FieldInput from '../../components/Inputs/field-input';

// --- helpers ---
const emailOK = (v) => !v || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v);
const telOK = (v) => !v || /^[0-9+()\-.\s]{6,}$/.test(v);
const fieldIsRequired = (f) => Boolean(f?.required ?? f?.validations?.required);
const isPdfFile = (f) =>
    !!f &&
    (
        (typeof f.type === 'string' && f.type.toLowerCase() === 'application/pdf') ||
        (typeof f.name === 'string' && /\.pdf$/i.test(f.name))
    );

const MAX_FILE_SIZE_MB = 5;
const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024;

const fileSizeOK = (file) => {
    return file instanceof File && file.size <= MAX_FILE_SIZE_BYTES;
};

const getServicesTotal = (services = []) => {
    return services.reduce((acc, service) => {
        return acc + Number(service.price || 0);
    }, 0);
};

const getFieldName = (key) => `fld_${key}`;

const normalizeId = (value) => {
    if (!value) return '';
    if (typeof value === 'object') return String(value._id || value.$oid || '');
    return String(value);
};

const NO_SUBDOMAIN_KEY = '__NO_SUBDOMAIN__';

const normalizeKey = (value) => normalizeId(value).trim().toLowerCase();

const getRequiredDocumentId = (requiredDoc) => {
    return normalizeId(
        requiredDoc?.documentId?._id ||
        requiredDoc?.documentId ||
        requiredDoc?._id ||
        requiredDoc
    );
};

const getCurrentSubdomainKeys = (formData) => {
    const keys = [
        formData?.subdomainId,
        formData?.subdomain,
        formData?.subdomain?._id,
        formData?.subdomain?.id,
        formData?.subdomain?.key,
        formData?.subdomain?.slug,
        formData?.subdomain?.name,
        formData?.subdomainData?._id,
        formData?.subdomainData?.id,
        formData?.subdomainData?.key,
        formData?.subdomainData?.slug,
        formData?.subdomainData?.name,
        formData?.subdomainData?.domain,
    ]
        .map(normalizeKey)
        .filter(Boolean);

    if (!keys.length) {
        keys.push(normalizeKey(NO_SUBDOMAIN_KEY));
    }

    return [...new Set(keys)];
};

const shouldAskDocumentForSubdomain = (requiredDoc, formData) => {
    const allowedSubdomains = Array.isArray(requiredDoc?.subdomains)
        ? requiredDoc.subdomains.map(normalizeKey).filter(Boolean)
        : [];

    // No condition = document shown everywhere
    if (allowedSubdomains.length === 0) return true;

    const currentSubdomainKeys = getCurrentSubdomainKeys(formData);

    return allowedSubdomains.some((subdomain) =>
        currentSubdomainKeys.includes(subdomain)
    );
};

const shouldAskFieldForSubdomain = (field, formData) => {
    const allowedSubdomains = Array.isArray(field?.subdomains)
        ? field.subdomains.map(normalizeKey).filter(Boolean)
        : [];

    // No restriction = field shown everywhere, including the main domain
    // and any subdomain created in the future.
    if (allowedSubdomains.length === 0) return true;

    const currentSubdomainKeys = getCurrentSubdomainKeys(formData);

    return allowedSubdomains.some((subdomain) =>
        currentSubdomainKeys.includes(subdomain)
    );
};

const shouldShowField = (field, formData) => {
    const condition = field?.conditionalDisplay;

    if (!condition?.fieldKey) return true;

    const watchedValue = formData[getFieldName(condition.fieldKey)];

    if (condition.operator === 'equals') {
        return watchedValue === condition.value;
    }

    if (condition.operator === 'not_equals') {
        return watchedValue !== condition.value;
    }

    return true;
};

// An unanswered trigger never activates a document, including "not equals".
const matchesDocumentCondition = (condition, values) => {
    if (!condition?.fieldKey) return true;
    const actual = values[condition.fieldKey];
    if (actual === undefined || actual === null || actual === '') return false;
    const equal = String(actual).trim() === String(condition.value ?? '').trim();
    return condition.operator === 'not_equals' ? !equal : equal;
};
const activeDocumentRules = (doc, formData) => (doc.rules || []).filter(rule =>
    matchesDocumentCondition(rule.conditionalDisplay,
        Object.fromEntries(Object.entries(formData).filter(([key]) => key.startsWith('fld_')).map(([key, value]) => [key.slice(4), value])))
);
const visibleDocuments = (docs, formData) => (docs || []).map(doc => {
    const rules = activeDocumentRules(doc, formData);
    return rules.length ? { ...doc, mandatory: rules.some(rule => rule.mandatory) } : null;
}).filter(Boolean);

const isFieldRequired = (field, formData) => {
    const isConditionallyDisplayed = Boolean(field?.conditionalDisplay?.fieldKey);

    if (isConditionallyDisplayed) {
        return shouldShowField(field, formData);
    }

    return Boolean(field?.required ?? field?.validations?.required);
};

export default function StepUploadFiles({ selectedServices, initialFormData, returnedData }) {
    const { t, i18n } = useTranslation();
    const currentLanguage = i18n.language;

    const [loading, setLoading] = useState(true);

    // unified sections => [{ kind, title(i18n), docs:[], fields:[] }]
    const [infoSections, setInfoSections] = useState([]);

    // Form state
    const [formData, setFormData] = useState({
        acceptDocuments: false,
        acceptCGV: false,
        ...initialFormData
    });

    const [formErrors, setFormErrors] = useState({});
    const [isSubmitting, setIsSubmitting] = useState(false);

    // ------------ Build Documents + Fields ------------
    useEffect(() => {
        const loadAll = async () => {
            // ----- documents
            const rawResult = await getDocuments();
            const allDocs = Array.isArray(rawResult) ? rawResult : rawResult.data || [];
            const docMap = new Map(
                allDocs.map(doc => [normalizeId(doc._id), doc])
            );

            const rawFields = await getFields();
            const allFields = Array.isArray(rawFields) ? rawFields : rawFields.data || [];
            const fieldsByKey = new Map(allFields.map(f => [f.key, f]));

            const docCount = {};
            const perServiceDocs = new Map(); // serviceId -> docs[]

            (selectedServices || []).forEach((service) => {
                const docs = (service.requiredDocuments || [])
                    .filter((requiredDoc) => shouldAskDocumentForSubdomain(requiredDoc, formData))
                    .map((requiredDoc) => {
                        const documentId = getRequiredDocumentId(requiredDoc);
                        const doc = docMap.get(documentId);

                        if (!doc) return null;

                        // IMPORTANT: the mandatory flag belongs to the service/document relation.
                        // A service can therefore make a globally mandatory document optional.
                        return {
                            ...doc,
                            mandatory: Boolean(requiredDoc?.mandatory ?? doc?.mandatory ?? true),
                            rules: [{ conditionalDisplay: requiredDoc?.conditionalDisplay, mandatory: Boolean(requiredDoc?.mandatory ?? doc?.mandatory ?? true) }],
                        };
                    })
                    .filter(Boolean);

                perServiceDocs.set(service._id, docs);

                docs.forEach((doc) => {
                    docCount[doc._id] = (docCount[doc._id] || 0) + 1;
                });
            });

            const generalDocs = [];
            const specificDocsByServiceId = new Map();
            (selectedServices || []).forEach((service) => {
                const list = perServiceDocs.get(service._id) || [];
                const specific = [];
                list.forEach(d => {
                    if (docCount[d._id] > 1) {
                        const existing = generalDocs.find(x => x._id === d._id);

                        if (!existing) {
                            generalDocs.push({ ...d });
                        } else {
                            // If at least one selected service requires the same document,
                            // keep it mandatory in the shared/general section.
                            existing.mandatory = Boolean(existing.mandatory || d.mandatory);
                            existing.rules.push(...d.rules);
                        }
                    } else {
                        specific.push(d);
                    }
                });
                specificDocsByServiceId.set(service._id, specific);
            });

            // ----- fields
            const countByKey = {};
            const firstByKey = new Map();

            (selectedServices || []).forEach((svc) => {
                const fields = Array.isArray(svc.requiredFields) ? svc.requiredFields : [];
                fields.forEach((f) => {
                    countByKey[f.key] = (countByKey[f.key] || 0) + 1;
                    if (!firstByKey.has(f.key)) firstByKey.set(f.key, f);
                });
            });

            const generalKeys = new Set(Object.entries(countByKey).filter(([, c]) => c > 1).map(([k]) => k));
            const generalFields = [...generalKeys]
                .map(k => {
                    const rf = firstByKey.get(k) || {};
                    const base = fieldsByKey.get(k) || {};
                    const merged = {
                        ...base,
                        ...rf,
                        conditionalDisplay: rf.conditionalDisplay?.fieldKey
                            ? rf.conditionalDisplay
                            : base.conditionalDisplay,
                        subdomains: Array.isArray(rf.subdomains)
                            ? rf.subdomains
                            : (Array.isArray(base.subdomains) ? base.subdomains : []),
                        options: (Array.isArray(rf.options) && rf.options.length)
                            ? rf.options
                            : (Array.isArray(base.options) ? base.options : []),
                    };

                    if (!shouldAskFieldForSubdomain(merged, formData)) return null;

                    return { ...merged, required: fieldIsRequired(merged) };
                })
                .filter(Boolean)
                .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));

            const specificFieldsByServiceId = new Map();

            (selectedServices || []).forEach((svc) => {
                const specificFields = (svc.requiredFields || [])
                    .filter(f => !generalKeys.has(f.key))
                    .map(rf => {
                        const base = fieldsByKey.get(rf.key) || {};

                        const merged = {
                            ...base,
                            ...rf,
                            conditionalDisplay: rf.conditionalDisplay?.fieldKey
                                ? rf.conditionalDisplay
                                : base.conditionalDisplay,
                            subdomains: Array.isArray(rf.subdomains)
                                ? rf.subdomains
                                : (Array.isArray(base.subdomains) ? base.subdomains : []),
                            options: (Array.isArray(rf.options) && rf.options.length)
                                ? rf.options
                                : (Array.isArray(base.options) ? base.options : []),
                        };

                        if (!shouldAskFieldForSubdomain(merged, formData)) return null;

                        return { ...merged, required: fieldIsRequired(merged) };
                    })
                    .filter(Boolean)
                    .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));

                specificFieldsByServiceId.set(svc._id, specificFields);
            });

            // ----- build unified sections
            const sections = [];

            // 1) General section
            if (generalDocs.length || generalFields.length) {
                sections.push({
                    kind: 'general',
                    title: {
                        fr: 'Informations générales',
                        en: 'General information',
                        de: 'Allgemeine Informationen'
                    },
                    docs: generalDocs,
                    fields: generalFields,
                });
            }

            // 2) Section per selected service
            (selectedServices || []).forEach((svc) => {
                const titleI18n = svc.title || {};

                sections.push({
                    kind: 'service',
                    serviceId: svc._id,
                    title: {
                        fr: `Informations pour : ${titleI18n.fr || titleI18n.en || titleI18n.de || ''}`,
                        en: `Information for: ${titleI18n.en || titleI18n.fr || titleI18n.de || ''}`,
                        de: `Informationen für: ${titleI18n.de || titleI18n.fr || titleI18n.en || ''}`,
                    },
                    docs: specificDocsByServiceId.get(svc._id) || [],
                    fields: specificFieldsByServiceId.get(svc._id) || [],
                });
            });

            setInfoSections(sections);

            // ----- init formData
            const initPatch = {};

            sections.forEach(section => {
                (section.docs || []).forEach(doc => {
                    const key = `doc_${doc._id}`;
                    if (!(key in formData)) initPatch[key] = null;
                });

                (section.fields || []).forEach(f => {
                    const key = `fld_${f.key}`;
                    if (!(key in formData)) {
                        initPatch[key] = f.type === 'checkbox' ? false : '';
                    }
                });
            });

            if (Object.keys(initPatch).length) {
                setFormData(prev => ({ ...initPatch, ...prev }));
            }

            setLoading(false);
        };

        setLoading(true);
        loadAll();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [JSON.stringify(selectedServices)]);

    // ------------ Handlers ------------
    const handleFileChange = (e) => {
        const { name, files } = e.target;
        const file = files && files[0];

        if (file instanceof File) {
            if (!isPdfFile(file)) {
                setFormData(prev => ({ ...prev, [name]: null }));
                setFormErrors(prev => ({
                    ...prev,
                    [name]: t('errors.onlyPdf') || 'PDF uniquement'
                }));
                return;
            }

            if (!fileSizeOK(file)) {
                setFormData(prev => ({ ...prev, [name]: null }));
                setFormErrors(prev => ({
                    ...prev,
                    [name]: `Le fichier ne doit pas dépasser ${MAX_FILE_SIZE_MB} Mo`
                }));
                return;
            }

            setFormData(prev => ({ ...prev, [name]: file }));
            setFormErrors(prev => ({ ...prev, [name]: undefined }));
        }
    };

    const handleCheckboxChange = (e) => {
        const { name, checked } = e.target;
        setFormData(prev => ({ ...prev, [name]: checked }));
        setFormErrors(prev => ({ ...prev, [name]: undefined }));
    };

    const handleFieldChange = (name, value) => {
        let computedNextFormData = null;

        setFormData(prev => {
            const next = { ...prev, [name]: value };

            infoSections.forEach(section => {
                (section.docs || []).forEach(doc => {
                    if (!activeDocumentRules(doc, next).length) next[`doc_${doc._id}`] = null;
                });
                (section.fields || []).forEach(field => {
                    const fieldName = `fld_${field.key}`;

                    if (!shouldShowField(field, next)) {
                        next[fieldName] = field.type === 'checkbox' ? false : '';
                    }
                });
            });

            computedNextFormData = next;
            return next;
        });

        setFormErrors(prev => {
            const next = { ...prev, [name]: undefined };
            const nextFormData = computedNextFormData || { ...formData, [name]: value };

            infoSections.forEach(section => {
                (section.docs || []).forEach(doc => {
                    if (!activeDocumentRules(doc, nextFormData).length) next[`doc_${doc._id}`] = undefined;
                });
                (section.fields || []).forEach(field => {
                    if (!shouldShowField(field, nextFormData)) {
                        next[`fld_${field.key}`] = undefined;
                    }
                });
            });

            return next;
        });
    };

    // ------------ Submit ------------
    const handleSubmit = async (e) => {
        e.preventDefault();
        const nextErrors = {};

        // Required documents (from unified sections)
        const requiredDocs = infoSections.flatMap(section => visibleDocuments(section.docs, formData));

        requiredDocs.forEach((doc) => {
            const name = `doc_${doc._id}`;
            const f = formData[name];
            const mandatory = Boolean(doc?.mandatory);

            // Optional document + no file selected => nothing to validate.
            if (!(f instanceof File)) {
                if (mandatory) {
                    nextErrors[name] = t('errors.required') || 'Ce fichier est requis';
                }
                return;
            }

            // If an optional document is provided, still validate its format and size.
            if (!isPdfFile(f)) {
                nextErrors[name] = t('errors.onlyPdf') || 'PDF uniquement';
                return;
            }

            if (!fileSizeOK(f)) {
                nextErrors[name] = `Le fichier ne doit pas dépasser ${MAX_FILE_SIZE_MB} Mo`;
            }
        });

        // Required fields (from unified sections)
        infoSections.forEach(section => {
            (section.fields || []).forEach(f => {
                if (!shouldShowField(f, formData)) return;

                const required = isFieldRequired(f, formData);
                const name = `fld_${f.key}`;
                const v = formData[name];

                const isEmpty =
                    v === undefined ||
                    v === null ||
                    String(v).trim() === '';

                if (required) {
                    if (f.type === 'checkbox') {
                        if (!v) nextErrors[name] = t('errors.required') || 'Ce champ est requis';

                    } else if (f.type === 'file') {
                        if (!(v instanceof File)) {
                            nextErrors[name] = t('errors.required') || 'Ce fichier est requis';

                        } else if (!isPdfFile(v)) {
                            nextErrors[name] = t('errors.onlyPdf') || 'PDF uniquement';

                        } else if (!fileSizeOK(v)) {
                            nextErrors[name] = `Le fichier ne doit pas dépasser ${MAX_FILE_SIZE_MB} Mo`;
                        }

                    } else if (isEmpty) {
                        nextErrors[name] = t('errors.required') || 'Ce champ est requis';
                    }
                }

                if (!nextErrors[name] && !isEmpty && f.type === 'email' && !emailOK(String(v))) {
                    nextErrors[name] = t('errors.invalidEmail') || 'Email invalide';
                }

                if (!nextErrors[name] && !isEmpty && f.type === 'tel' && !telOK(String(v))) {
                    nextErrors[name] = t('errors.invalidPhone') || 'Numéro invalide';
                }

                if (!nextErrors[name] && !isEmpty && f.type === 'number' && Number.isNaN(Number(v))) {
                    nextErrors[name] = t('errors.invalidNumber') || 'Nombre invalide';
                }

                if (!nextErrors[name] && !isEmpty && f.validations?.pattern) {
                    const regex = new RegExp(f.validations.pattern);

                    if (!regex.test(String(v).trim())) {
                        nextErrors[name] = t('errors.invalidFormat') || 'Format invalide';
                    }
                }
            });
        });

        // Accept checkboxes
        if (!formData.acceptDocuments) nextErrors.acceptDocuments = t('errors.acceptDocuments');
        if (!formData.acceptCGV) nextErrors.acceptCGV = t('errors.acceptCGV');

        setFormErrors(nextErrors);
        if (Object.keys(nextErrors).length > 0) {
            Helper.toast(t('errors.formInvalid') || t('errors.acceptCheckbox') || 'Veuillez corriger les erreurs.', 'error');
            return;
        }

        // Build fields payload (plain map key -> value)
        const submissionFormData = { ...formData };
        infoSections.forEach(section => (section.docs || []).forEach(doc => {
            if (!activeDocumentRules(doc, formData).length) delete submissionFormData[`doc_${doc._id}`];
        }));
        const fieldsPayload = {};
        Object.entries(formData).forEach(([k, v]) => {
            if (k.startsWith('fld_')) fieldsPayload[k.replace(/^fld_/, '')] = v;
        });

        setIsSubmitting(true);
        try {
            const response = await submitFilesOnly(formData._id, { ...submissionFormData, fieldsPayload }, t);
            const res = response.data;

            if (res.status) {
                const submissionId =
                    res.submission?._id ||
                    res.data?._id ||
                    res._id ||
                    formData._id;

                const nextFormData = {
                    ...formData,
                    ...res.submission,
                    _id: submissionId,
                    fieldsPayload
                };

                const total = getServicesTotal(nextFormData.services || formData.services || []);

                if (Number(total) === 0) {
                    try {
                        const freeResponse = await validateFreeService(submissionId, t);
                        const freeResult = freeResponse.data;

                        if (freeResult.status) {
                            Helper.toast('Votre demande a été validée avec succès.', 'success');

                            returnedData({
                                ...nextFormData,
                                ...(freeResult.submission || {}),
                                _id: freeResult.submission?._id || submissionId,
                                step: 'paid'
                            });

                            return;
                        }

                        console.error('Free validation error:', freeResult);
                        Helper.toast(freeResult.message || 'Erreur lors de la validation gratuite.', 'error');
                        return;

                    } catch (freeError) {
                        console.error('Free validation submit error:', freeError);
                        Helper.toast('Erreur lors de la validation gratuite.', 'error');
                        return;
                    }
                }

                returnedData({
                    ...nextFormData,
                    step: 'step4'
                });

            } else {
                console.error('API error:', res);
            }
        } catch (error) {
            console.error('Submit error:', error);
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <>
            <TitleQuotation title={t('pageServices.step3.welcomeMessage')} description={t('pageServices.step3.description')} step="3" />
            <Hr />

            {loading ? (
                <div>
                    <div className="py-12"><Loading /></div>
                    <div className="text-center text-gray-500 py-4">{t('documents.loading')}</div>
                    <Hr />
                </div>
            ) : (
                <div className="w-full max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="mt-8 space-y-10">
                        {/* --------- FULL-WIDTH TITLES + CONTENT --------- */}
                        {infoSections.map((section, idx) => {
                            const title =
                                section.title?.[currentLanguage] || section.title?.fr || section.title || '';

                            return (
                                <React.Fragment key={`sec-${idx}`}>
                                    {/* Full-width title */}
                                    <h2 className="text-lg font-bold text-primary">{title}</h2>

                                    {/* Content: files first (compact), then fields */}
                                    <div className="flex flex-col gap-6">
                                        {visibleDocuments(section.docs, formData).length > 0 && (
                                            <DocumentUploadSection
                                                compact
                                                title=""
                                                formData={formData}
                                                handleFileChange={handleFileChange}
                                                documents={visibleDocuments(section.docs, formData).map(doc => ({
                                                    label: doc.name?.[currentLanguage] || doc.name?.fr || '',
                                                    name: `doc_${doc._id}`,
                                                    description: doc.description?.[currentLanguage] || doc.description?.fr || '',
                                                    downloadLink: doc.link?.[currentLanguage] || doc.link?.fr || '',
                                                    downloadLabel: (doc.link?.[currentLanguage] || doc.link?.fr) ? t('documents.downloadTemplate') : '',
                                                    italicNote: doc.italicNote,
                                                    mandatory: Boolean(doc.mandatory),
                                                    required: Boolean(doc.mandatory)
                                                }))}
                                                formErrors={formErrors}
                                            />
                                        )}

                                        {section.fields
                                            .filter((f) => shouldShowField(f, formData))
                                            .map((f) => {
                                                const computedRequired = isFieldRequired(f, formData);

                                                return (
                                                    <FieldInput
                                                        key={f.key}
                                                        def={{
                                                            ...f,
                                                            required: computedRequired,
                                                            validations: {
                                                                ...(f.validations || {}),
                                                                required: computedRequired,
                                                            },
                                                        }}
                                                        lang={currentLanguage}
                                                        value={formData[`fld_${f.key}`]}
                                                        error={formErrors[`fld_${f.key}`]}
                                                        onChange={handleFieldChange}
                                                    />
                                                );
                                            })}
                                    </div>

                                    {idx < infoSections.length - 1 && <Hr />}
                                </React.Fragment>
                            );
                        })}

                        {/* ---------- ACCEPT CHECKBOXES ---------- */}
                        <div className="mt-2 space-y-2">
                            <label className="flex items-start space-x-2">
                                <input
                                    type="checkbox"
                                    name="acceptDocuments"
                                    checked={formData.acceptDocuments || false}
                                    onChange={handleCheckboxChange}
                                    className="accent-primary mt-1"
                                />
                                <span className="text-sm text-gray-800">
                                    {t('documents.acceptDocuments')}
                                </span>
                            </label>
                            {formErrors.acceptDocuments && (
                                <p className="text-sm text-red-500 ml-6">{formErrors.acceptDocuments}</p>
                            )}

                            <label className="flex items-start space-x-2">
                                <input
                                    type="checkbox"
                                    name="acceptCGV"
                                    checked={formData.acceptCGV || false}
                                    onChange={handleCheckboxChange}
                                    className="accent-primary mt-1"
                                />
                                <span className="text-sm text-gray-800">
                                    {t('documents.acceptCGV')}{' '}
                                    <a
                                        href="https://storage.googleapis.com/fiska-public/FISKA/CGV-FISKAGROUP.pdf"
                                        target="_blank"
                                        rel="noreferrer"
                                        className="text-primary underline"
                                    >
                                        {t('documents.terms')}
                                    </a>
                                </span>
                            </label>
                            {formErrors.acceptCGV && (
                                <p className="text-sm text-red-500 ml-6">{formErrors.acceptCGV}</p>
                            )}
                        </div>

                        {/* ---------- NAV ---------- */}
                        <div className="flex justify-between mt-10">
                            <button
                                type="button"
                                onClick={() => returnedData({ ...formData, step: 'step2' })}
                                className="btn btn-white border border-primary text-primary text-sm py-3 px-6 rounded-xl hover:bg-primary hover:text-white transition"
                            >
                                {t('inputs.back')}
                            </button>

                            <button
                                type="submit"
                                onClick={handleSubmit}
                                disabled={isSubmitting}
                                className="btn btn-primary bg-primary text-white text-sm py-3 px-6 rounded-xl hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition flex items-center gap-2"
                            >
                                {isSubmitting ? (
                                    <>
                                        <span className="inline-block h-4 w-4 rounded-full border-2 border-white border-t-transparent animate-spin" />
                                        <span>Envoi des documents...</span>
                                    </>
                                ) : (
                                    t('inputs.nextStep')
                                )}
                            </button>
                        </div>
                        {isSubmitting && (
                            <div className="mt-4 rounded-xl border border-blue-100 bg-blue-50 px-4 py-3 text-sm text-blue-700">
                                Vos documents sont en cours d’envoi. Merci de ne pas fermer cette page.
                            </div>
                        )}
                    </div>
                </div>
            )}
        </>
    );
}